/**
 * EntryDossier — a ficha de arquivo do Universo. Ao clicar num personagem
 * (ou local/lore), uma folha de papel é "puxada do arquivo" com animação
 * cinematográfica: sobe com leve rotação e assenta como um documento de
 * dossiê — foto polaroide, carimbo, dados datilografados.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Pencil, Trash2, X, Camera } from 'lucide-react';
import type { BibleEntry } from '../lib/api';

const TYPE_GRADIENT: Record<string, string> = {
  character: 'linear-gradient(135deg,#7EA4F5,#5B8DEF)',
  location: 'linear-gradient(135deg,var(--aurora),#2E9E8A)',
  lore: 'linear-gradient(135deg,#C4ABFC,#A78BFA)',
  item: 'linear-gradient(135deg,var(--ember-hot),var(--ember))',
  organization: 'linear-gradient(135deg,#A8B8E8,#6B7BA8)',
  note: 'linear-gradient(135deg,#8B9AC8,#5C6890)',
};

export function EntryDossier({
  entry,
  typeLabel,
  onClose,
  onEdit,
  onDelete,
  onEditImage,
}: {
  entry: BibleEntry | null;
  /** Rótulo traduzido do tipo (Personagem, Local…). */
  typeLabel: (e: BibleEntry) => string;
  onClose: () => void;
  /** When omitted, the dossier is read-only and hides the edit/delete actions. */
  onEdit?: (e: BibleEntry) => void;
  onDelete?: (e: BibleEntry) => void;
  /** When provided, shows a button on the photo to generate/replace the image. */
  onEditImage?: (e: BibleEntry) => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!entry) return;
    const h = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose(); };
    addEventListener('keydown', h);
    return () => removeEventListener('keydown', h);
  }, [entry, onClose]);

  return (
    <AnimatePresence>
      {entry && (
        <motion.div
          key="dossier-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.22 } }}
          className="fixed inset-0 z-[130] grid place-items-center bg-[rgba(4,7,14,0.8)] backdrop-blur-md p-4 cursor-zoom-out"
          onClick={onClose}
        >
          {/* a folha puxada do arquivo */}
          <motion.div
            key={entry.id}
            initial={{ y: '68vh', rotate: 7, scale: 0.94, opacity: 0 }}
            animate={{ y: 0, rotate: -1.1, scale: 1, opacity: 1 }}
            exit={{ y: '68vh', rotate: 5, opacity: 0, transition: { duration: 0.28, ease: [0.4, 0, 0.9, 0.4] } }}
            transition={{ type: 'spring', stiffness: 120, damping: 16, mass: 0.9 }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex h-[min(620px,84dvh)] w-[min(560px,94vw)] flex-col overflow-hidden cursor-default rounded-[3px_10px_10px_3px] px-7 py-7 md:px-9 md:py-8"
            style={{
              background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
              boxShadow: '0 60px 120px -24px rgba(0,0,0,0.85), 0 0 0 1px rgba(70,55,30,0.25), inset -14px 0 22px -16px rgba(70,55,30,0.35)',
            }}
          >
            {/* fitas adesivas */}
            <span aria-hidden className="pointer-events-none absolute -top-2 left-8 h-5 w-16 rotate-[-6deg] bg-white/35 shadow-sm" />
            <span aria-hidden className="pointer-events-none absolute -top-2 right-10 h-5 w-14 rotate-[5deg] bg-white/30 shadow-sm" />

            {/* fechar */}
            <button
              onClick={onClose}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-[#7A6F52] hover:bg-[rgba(42,36,56,0.08)] transition-colors"
              aria-label={t('common.close', 'Fechar')}
            >
              <X size={15} />
            </button>

            {/* carimbo do arquivo */}
            <p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.3em] text-[#A8935F]">
              {t('storyBible.dossier', 'Arquivo do Universo')} · {typeLabel(entry)}
            </p>
            <p className="mt-0.5 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]/70">
              Nº {entry.id.slice(0, 8).toUpperCase()}
            </p>

            <div className="mt-4 flex shrink-0 items-start gap-5">
              <div className="min-w-0 flex-1">
                <h2 className="font-serif text-[30px] font-medium leading-[1.05] text-[#241C36]">
                  {entry.name}
                </h2>
                <div className="mt-3 h-px bg-[rgba(70,55,30,0.2)]" />
              </div>

              {/* polaroide — foto real ou monograma */}
              <motion.div
                initial={{ rotate: 6, y: -8, opacity: 0 }}
                animate={{ rotate: 2.5, y: 0, opacity: 1 }}
                transition={{ delay: 0.18, type: 'spring', stiffness: 160, damping: 15 }}
                className="relative w-[124px] shrink-0 bg-white p-1.5 pb-6 shadow-[0_14px_28px_-10px_rgba(70,55,30,0.55)]"
              >
                {/* selo do tipo, escrito à mão na moldura */}
                <span className="absolute bottom-1 left-0 right-0 text-center font-serif italic text-[10px] text-[#7A6F52]">
                  {typeLabel(entry)}
                </span>
                {/* trocar/gerar a foto — abre o estúdio de imagem */}
                {onEditImage && (
                  <button
                    onClick={() => onEditImage(entry)}
                    className="absolute -right-2 -top-2 grid h-8 w-8 place-items-center rounded-full bg-[#2A2438] text-[#F6F1E4] shadow-[0_3px_0_#16121F] transition-transform hover:-translate-y-px active:scale-95"
                    title={t('storyBible.imageStudioTitle', 'Imagem (gerar / trocar)')}
                    aria-label={t('storyBible.imageStudioTitle', 'Imagem (gerar / trocar)')}
                  >
                    <Camera size={14} />
                  </button>
                )}
                {entry.imageUrl ? (
                  <img src={entry.imageUrl} alt={entry.name} className="aspect-square w-full object-cover" />
                ) : (
                  <div
                    className="grid aspect-square w-full place-items-center font-serif text-[42px] font-semibold text-[#070B16]"
                    style={{ background: TYPE_GRADIENT[entry.type] ?? TYPE_GRADIENT.note }}
                  >
                    {entry.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </motion.div>
            </div>

            {/* miolo — descrição + atributos rolam juntos, barra OCULTA, sem
                estourar o papel (nem Y nem X) */}
            <div className="no-scrollbar mt-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              {entry.content ? (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="whitespace-pre-wrap break-words font-serif text-[14px] leading-[1.75] text-[#4E4430] [background-image:repeating-linear-gradient(transparent,transparent_calc(1.75em-1px),rgba(70,55,30,0.09)_calc(1.75em-1px),rgba(70,55,30,0.09)_1.75em)]"
                >
                  {entry.content}
                </motion.p>
              ) : (
                <p className="font-serif italic text-[13px] text-[#7A6F52]">
                  {t('storyBible.dossierEmpty', 'Ficha ainda sem anotações.')}
                </p>
              )}

              {/* atributos datilografados */}
              {entry.attributes && Object.keys(entry.attributes).length > 0 && (
                <div className="mt-5 border-t border-[rgba(70,55,30,0.18)] pt-3">
                  {Object.entries(entry.attributes).map(([k, v], ai) => (
                    <motion.div
                      key={k}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + ai * 0.12, duration: 0.35 }}
                      className="flex justify-between gap-6 py-1 text-[11.5px] text-[#5C5138]">
                      <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.16em]">{k}</span>
                      <b className="min-w-0 break-words text-right font-semibold text-[#2A2438]">{v}</b>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* ações da ficha */}
            <div className="mt-6 flex shrink-0 items-center justify-between border-t border-[rgba(70,55,30,0.18)] pt-4">
              <p className="font-mono text-[7.5px] uppercase tracking-[0.18em] text-[#A8935F]">
                {t('storyBible.dossierHint', 'toque fora para guardar a ficha')}
              </p>
              {(onEdit || onDelete) && (
                <div className="flex gap-2">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(entry)}
                      className="grid h-9 w-9 place-items-center rounded-[10px] bg-[#2A2438] text-[#F6F1E4] shadow-[0_3px_0_#16121F] transition-transform hover:-translate-y-px active:scale-95"
                      title={t('storyBible.editTitle', 'Editar')}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(entry)}
                      className="grid h-9 w-9 place-items-center rounded-[10px] bg-[rgba(42,36,56,0.05)] text-[#8A3B2E] shadow-[0_0_0_1.5px_rgba(138,59,46,0.35)_inset] transition-colors hover:bg-[rgba(138,59,46,0.1)] active:scale-95"
                      title={t('storyBible.deleteTitle', 'Excluir')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
